// ark-migration-status=migrated
import {
  AddressField as SailAddressField,
  view,
  createViewConfig,
  FormFieldGroupConfig,
  TextFieldConfig,
} from '@sail/ui';
import React from 'react';
import type {AddressFieldCountryCode, AddressValueAdapter} from '@sail/ui';
import type {
  AddressValueAdapter as AddressValueAdapterWithOverrideFields,
  SchemaOverride,
} from '@stripe-internal/connect-embedded-lib';
import {assignProps} from '@sail/engine';
import {
  subRegionOverrides,
  SailCustomisableAddressField,
  formatAddressPlaceholders,
  generateCountryIntlMap,
} from '@stripe-internal/connect-embedded-lib';
import {useIntl} from '@sail/intl';
import {useFlag} from '@sail/access';
import {useArkFormContext} from '@stripe-internal/ark-lib';
import type {AddressCollectionSurface} from '../../accounts/formFields/helpers/ArkAddressField';
import useRetrieveIpGeocodeQuery from '../../../graphql/queries/useRetieveIpGeocodeQuery';
import {AddressAutocompleteIframe} from './AddressAutocompleteIframe';
import {useSupportedMapboxCountries} from './useSupportedMapboxCountries';

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const AddressFieldValueKeysArr = [
  'line1',
  'line2',
  'line3',
  'line4',
  'town',
  'city',
  'state',
  'postal_code',
  'country',
] as const;

export type AddressFieldValueKeys = (typeof AddressFieldValueKeysArr)[number];

export type AddressFieldValueBase<T> = Partial<
  Record<AddressFieldValueKeys, T>
>;

export type AddressFieldValue = AddressFieldValueBase<string> & {
  country: AddressFieldCountryCode;
};

type AddressFieldFunction = AddressFieldValueBase<(input?: string) => void> & {
  country: (input?: AddressFieldCountryCode) => void;
};
export type AddressFieldProps = Partial<{
  country: AddressFieldCountryCode;
  disabled: boolean;
  // No support for field level errors yet
  errors: AddressFieldValueBase<React.ReactNode>;
  onBlur: AddressFieldFunction;
  onChange: AddressFieldFunction;
  value: AddressFieldValue;
  // autoFocus: boolean;
  countryAllowList: AddressFieldCountryCode[];
  // No Support for forcing country
  forceShowCountry: boolean;
  enableAddressAutocomplete?: boolean;
  adapter?: AddressValueAdapter<AddressFieldValue>;
  // Adapter used if SailCustomisableAddressField is used
  customizableAdapter?: AddressValueAdapterWithOverrideFields<AddressFieldValue>;
  surface?: AddressCollectionSurface;
  schemaOverride?: SchemaOverride;
  size?: 'small' | 'medium' | 'large';
  // The spacing between each field (https://sail.stripe.me/tokens/space/)
  gap?:
    | 'xxsmall'
    | 'xsmall'
    | 'small'
    | 'medium'
    | 'large'
    | 'xlarge'
    | 'xxlarge';
}>;

const defaultAdapter: AddressValueAdapter<AddressFieldValue> = {
  toAddressValue: (value) => {
    return {
      address: value.line1,
      address2: value.line2,
      locality: value.city,
      subregion: value.state,
      postalCode: value.postal_code,
      countryCode: value.country,
    };
  },
  fromAddressValue: (value) => {
    return {
      line1: value.address ?? '',
      line2: value.address2 ?? '',
      city: value.locality ?? '',
      state: value.subregion ?? '',
      postal_code: value.postalCode ?? '',
      country: value.countryCode ?? '',
    };
  },
  unsupportedFields: ['dependentLocality', 'sortingCode'],
};

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const ARKAddressFieldConfig = createViewConfig({
  props: {} as AddressFieldProps,
  name: 'BackwardsCompatibleAddressField',
  defaults: {
    enableAddressAutocomplete: true,
  },
});
export const BackwardsCompatibleAddressField = ARKAddressFieldConfig.createView(
  ({
    onChange,
    countryAllowList,
    country,
    onBlur,
    value,
    forceShowCountry = true,
    errors,
    enableAddressAutocomplete,
    adapter = defaultAdapter,
    customizableAdapter = defaultAdapter as AddressValueAdapterWithOverrideFields<AddressFieldValue>,
    surface,
    schemaOverride,
    size = 'medium',
    gap = 'xsmall',
    ...props
  }) => {
    const intl = useIntl();
    const mapboxSupportedCountries = useSupportedMapboxCountries();
    const {country: merchantCountry} = useArkFormContext();

    // More info here - https://docs.google.com/document/d/1fxrfRS6ySBUJjUCgH56m4SM-dINYozf_7PUXg6Bzys0/edit
    // Customisable address is enabled for MX and IN, different flags since country prop in flag has issues
    // See this PR Comment for more info - https://git.corp.stripe.com/stripe-internal/pay-server/pull/850982#pullrequestreview-4535718
    const customizableAddressEnabledForCountry =
      (merchantCountry === 'MX' &&
        useFlag('enable_customisable_onboarding_address_component')) ||
      merchantCountry === 'IN';
    const useCustomizableAddress =
      surface === 'onboarding' && customizableAddressEnabledForCountry;

    const enableMapboxAutocomplete =
      enableAddressAutocomplete &&
      mapboxSupportedCountries.includes(
        (value?.country || country || '').toUpperCase(),
      );

    const {data, loading, error} = useRetrieveIpGeocodeQuery({
      skip: !enableMapboxAutocomplete,
    });
    const {lat, lon} = data?.v1IpGeocode ?? {};
    const latitude = lat ? Number(lat) : undefined;
    const longitude = lon ? Number(lon) : undefined;

    let countryAllowListProper: AddressFieldCountryCode[] | undefined;
    if (countryAllowList) {
      countryAllowListProper = countryAllowList;
    }
    if (country) {
      countryAllowListProper = [country];
    }

    const makeUses = (key: AddressFieldValueKeys) => {
      const onBlurProp = onBlur?.[key]
        ? {
            onBlur: () => onBlur?.[key]?.(),
          }
        : {};
      const error = errors?.[key];

      // address line1 is not a TextField, so onBlur prop cannot be passed directly
      return key === 'line1'
        ? [
            TextFieldConfig.customize({
              uses: [
                assignProps({
                  ...onBlurProp,
                }),
              ],
            }),
            assignProps({
              error,
            }),
          ]
        : [
            assignProps({
              ...onBlurProp,
              error,
            }),
          ];
    };

    return (
      // This view is needed. Without it, the subviews in AddressField do not work?
      <view.div>
        <AddressAutocompleteIframe
          useMapboxAutocomplete={enableMapboxAutocomplete ?? false}
        >
          {(autocomplete) => (
            <FormFieldGroupConfig.Customize
              subviews={{
                inputs: {
                  css: {gap},
                },
              }}
            >
              {useCustomizableAddress ? (
                <SailCustomisableAddressField
                  data-testid="SailCustomisableAddressField"
                  preferHideCountrySelector={!forceShowCountry}
                  countryAllowlist={countryAllowListProper}
                  value={value}
                  adapter={customizableAdapter}
                  size={size}
                  unstable_labelsOverrides={{
                    address: formatAddressPlaceholders(intl),
                  }}
                  {...props}
                  subviews={{
                    address1: {
                      uses: makeUses('line1'),
                    },
                    address2: {
                      uses: makeUses('line2'),
                    },
                    subregion: {
                      uses: makeUses('state'),
                    },
                    locality: {
                      uses: makeUses('city'),
                    },
                    postalCode: {
                      uses: makeUses('postal_code'),
                    },
                    countryCode: {
                      uses: makeUses('country'),
                    },
                  }}
                  onChange={(address) => {
                    AddressFieldValueKeysArr.forEach((key) => {
                      if (address[key] !== value?.[key]) {
                        onChange?.[key]?.(address[key]);
                      }
                    });
                  }}
                  schemaOverride={schemaOverride}
                  autoComplete={!enableMapboxAutocomplete}
                  onAutocomplete={async (query) => {
                    if (loading || error || !value?.country) {
                      return [];
                    }
                    const guesses =
                      (await autocomplete(query, value.country, {
                        latitude,
                        longitude,
                      })) ?? [];
                    return guesses.map((guess) => ({
                      address: guess.name,
                      addressValue: defaultAdapter.toAddressValue({
                        ...guess.address,
                        country: value.country,
                      }),
                    }));
                  }}
                />
              ) : (
                <SailAddressField
                  data-testid="SailAddressField"
                  preferHideCountrySelector={!forceShowCountry}
                  countryAllowlist={countryAllowListProper}
                  value={value}
                  adapter={adapter}
                  size={size}
                  unstable_labelsOverrides={{
                    address: formatAddressPlaceholders(intl),
                    country: generateCountryIntlMap(intl),
                    subregion: subRegionOverrides,
                  }}
                  {...props}
                  subviews={{
                    address1: {
                      uses: makeUses('line1'),
                    },
                    address2: {
                      uses: makeUses('line2'),
                    },
                    subregion: {
                      uses: makeUses('state'),
                    },
                    locality: {
                      uses: makeUses('city'),
                    },
                    postalCode: {
                      uses: makeUses('postal_code'),
                    },
                    countryCode: {
                      uses: makeUses('country'),
                    },
                  }}
                  onChange={(address) => {
                    AddressFieldValueKeysArr.forEach((key) => {
                      if (address[key] !== value?.[key]) {
                        onChange?.[key]?.(address[key]);
                      }
                    });
                  }}
                  autoComplete={!enableMapboxAutocomplete}
                  onAutocomplete={async (query) => {
                    if (loading || error || !value?.country) {
                      return [];
                    }
                    const guesses =
                      (await autocomplete(query, value.country, {
                        latitude,
                        longitude,
                      })) ?? [];
                    return guesses.map((guess) => ({
                      address: guess.name,
                      addressValue: defaultAdapter.toAddressValue({
                        ...guess.address,
                        country: value.country,
                      }),
                    }));
                  }}
                />
              )}
            </FormFieldGroupConfig.Customize>
          )}
        </AddressAutocompleteIframe>
      </view.div>
    );
  },
);
export default BackwardsCompatibleAddressField;
